import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Image as ImageIcon, Loader2, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import { botsApi } from '../../services/api';
import { saveFile } from '../../native/files';
import { isNative } from '../../native/platform';

export const MAX_FILES = 5;
export const MAX_BYTES = 10 * 1024 * 1024;
export const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);

// KB or MB in the viewer's language ("kB", "ko"), from Intl.
const size = (bytes, lang) => {
  const [value, unit, digits] = bytes < 1024 * 1024
    ? [Math.max(1, Math.round(bytes / 1024)), 'kilobyte', 0] : [bytes / 1048576, 'megabyte', 1];
  try {
    return new Intl.NumberFormat(lang, {
      style: 'unit', unit, unitDisplay: 'short', minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${value.toFixed(digits)} ${unit === 'kilobyte' ? 'KB' : 'MB'}`;
  }
};

/**
 * Files waiting to go with the next message. Each file uploads as soon as it
 * is picked, dropped or pasted, so sending is instant.
 */
export const useAttachments = (botId, projectId) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const patch = (key, fields) => setItems((all) => all.map((x) => (x.key === key ? { ...x, ...fields } : x)));

  const add = useCallback((fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;  // no projectId: a chat in no project
    const room = MAX_FILES - itemsRef.current.length;
    if (picked.length > room) toast.error(t('bots.files.max', { count: MAX_FILES }));
    picked.slice(0, Math.max(0, room)).forEach((file) => {
      if (file.size > MAX_BYTES) { toast.error(t('bots.files.too_big', { name: file.name })); return; }
      const key = `${Date.now()}-${Math.random()}`;
      const preview = IMAGE_TYPES.has(file.type) ? URL.createObjectURL(file) : null;
      setItems((all) => [...all, { key, name: file.name || 'file', size: file.size, preview, progress: 0, id: null }]);
      botsApi.uploadFile(botId, projectId, file, (progress) => patch(key, { progress }))
        .then((r) => patch(key, { id: r.data.id, progress: 1 }))
        .catch((err) => {
          toast.error(err?.response?.status === 413 ? t('bots.files.too_big', { name: file.name }) : t('bots.files.failed', { name: file.name }));
          setItems((all) => all.filter((x) => x.key !== key));
        });
    });
  }, [botId, projectId, t]);

  const remove = (key) => setItems((all) => {
    const gone = all.find((x) => x.key === key);
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    return all.filter((x) => x.key !== key);
  });
  const clear = useCallback(() => setItems((all) => {
    all.forEach((x) => x.preview && URL.revokeObjectURL(x.preview));
    return [];
  }), []);

  // A different project gets its own files.
  useEffect(() => clear, [projectId, clear]);

  return {
    items, add, remove, clear,
    ids: items.filter((x) => x.id).map((x) => x.id),
    uploading: items.some((x) => !x.id),
  };
};

export const AttachButton = ({ onPick, disabled }) => {
  const { t } = useTranslation();
  const input = useRef(null);
  return (
    <>
      <button type="button" onClick={() => input.current?.click()} disabled={disabled} title={t('bots.files.attach')}
        aria-label={t('bots.files.attach')} data-testid="bot-attach"
        className="inline-flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-30">
        <Paperclip className="w-4 h-4" />
      </button>
      <input ref={input} type="file" multiple className="hidden" data-testid="bot-attach-input"
        onChange={(e) => { onPick(e.target.files); e.target.value = ''; }} />
    </>
  );
};

/** The files in the composer, before sending. */
export const PendingFiles = ({ items, onRemove }) => {
  const { t, i18n } = useTranslation();
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2 px-3 pt-3" data-testid="bot-pending-files">
      {items.map((x) => (
        <div key={x.key} className="relative flex items-center gap-2 max-w-[220px] rounded-xl border border-border bg-background pl-1.5 pr-7 py-1.5">
          {x.preview ? <img src={x.preview} alt="" className="w-9 h-9 rounded-lg object-cover" />
            : <span className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center"><FileText className="w-4 h-4 text-muted-foreground" /></span>}
          <span className="min-w-0">
            <span className="block text-xs text-foreground truncate">{x.name}</span>
            <span className="block text-[10px] text-muted-foreground">
              {x.id ? size(x.size, i18n.language) : <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{Math.round(x.progress * 100)}%</span>}
            </span>
          </span>
          <button type="button" onClick={() => onRemove(x.key)} title={t('bots.files.remove')}
            className="absolute top-1 right-1 p-0.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent">
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
};

// Blob URLs of files already shown, so scrolling back does not download again.
const blobUrls = new Map();
const loadBlob = async (fileId) => {
  if (!blobUrls.has(fileId)) {
    blobUrls.set(fileId, botsApi.fileBlob(fileId).then((r) => URL.createObjectURL(r.data)).catch((e) => {
      blobUrls.delete(fileId);
      throw e;
    }));
  }
  return blobUrls.get(fileId);
};

const Thumb = ({ file, onOpen }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    loadBlob(file.id).then((u) => { if (alive) setUrl(u); }).catch(() => {});
    return () => { alive = false; };
  }, [file.id]);
  return (
    <button type="button" onClick={onOpen} title={file.name}
      className="block overflow-hidden rounded-2xl border border-border bg-muted w-40 h-28 sm:w-48 sm:h-32">
      {url ? <img src={url} alt={file.name} className="w-full h-full object-cover" />
        : <span className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-muted-foreground" /></span>}
    </button>
  );
};

/** Files sent with a message: images as pictures, other files as cards. */
export const MessageFiles = ({ files }) => {
  const { t, i18n } = useTranslation();
  if (!files?.length) return null;
  const open = async (file) => {
    try {
      const url = await loadBlob(file.id);
      if (isNative()) {  // the app has no tabs: the share sheet opens or saves it
        await saveFile(await (await fetch(url)).blob(), file.name);
      } else if (file.kind === 'image' || file.kind === 'pdf') {
        window.open(url, '_blank', 'noopener');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
      }
    } catch {
      toast.error(t('bots.files.open_failed'));
    }
  };
  return (
    <div className="flex flex-wrap items-start justify-end gap-2" data-testid="bot-message-files">
      {files.map((f) => (f.kind === 'image' ? <Thumb key={f.id} file={f} onOpen={() => open(f)} /> : (
        <button key={f.id} type="button" onClick={() => open(f)}
          className="flex items-center gap-2 max-w-[260px] rounded-2xl border border-border bg-card px-3 py-2 text-left hover:bg-accent">
          <span className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-muted-foreground" /></span>
          <span className="min-w-0">
            <span className="block text-xs text-foreground truncate">{f.name}</span>
            <span className="block text-[10px] text-muted-foreground">
              {size(f.size, i18n.language)}{f.pages ? ` · ${t('bots.files.pages', { count: f.pages })}` : ''}
            </span>
          </span>
        </button>
      )))}
    </div>
  );
};
